import { lazy, Suspense } from "react";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import PlatformsSection from "@/components/PlatformsSection";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";
import { Helmet } from "react-helmet-async";

// Lazy load below-the-fold components for faster initial paint
const PricingSection = lazy(() => import("@/components/PricingSection"));
const PriceComparisonSection = lazy(() => import("@/components/PriceComparisonSection"));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const FaqSection = lazy(() => import("@/components/FaqSection"));
const SeoContentSection = lazy(() => import("@/components/SeoContentSection"));
const Footer = lazy(() => import("@/components/Footer"));
const FloatingTestCTA = lazy(() => import("@/components/FloatingTestCTA"));

// Simple loading fallback
const SectionFallback = () => <div className="h-64 bg-background" />;

// Données structurées JSON-LD pour le SEO (static - outside component for performance)
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "RoiDuStreaming",
  "url": "https://roidustreaming.fr",
  "logo": "https://roidustreaming.fr/favicon.png",
  "description": "Service IPTV premium avec +100 000 films, +25 000 séries et +16 000 chaînes TV en 4K HDR.",
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "availableLanguage": ["French", "English"]
  },
  "sameAs": ["https://t.me/roidustreaming"]
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "RoiDuStreaming",
  "url": "https://roidustreaming.fr",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://roidustreaming.fr/?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

const productSchema = {
  "@context": "https://schema.org",
  "@type": "Product",
  "name": "Abonnement IPTV Premium RoiDuStreaming",
  "description": "Accès illimité à +100 000 films, +25 000 séries, +16 000 chaînes TV en 4K HDR. Compatible Smart TV, smartphone, tablette, PC.",
  "brand": { "@type": "Brand", "name": "RoiDuStreaming" },
  "offers": [
    { "@type": "Offer", "name": "Pass VIP 3 Mois", "price": "24.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://roidustreaming.fr/nos-offres" },
    { "@type": "Offer", "name": "Pass VIP 6 Mois", "price": "34.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://roidustreaming.fr/nos-offres" },
    { "@type": "Offer", "name": "Pass VIP 12 Mois", "price": "59.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://roidustreaming.fr/nos-offres" }
  ],
  "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "2847", "bestRating": "5", "worstRating": "1" }
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    { "@type": "Question", "name": "Combien de temps pour recevoir mes identifiants ?", "acceptedAnswer": { "@type": "Answer", "text": "Immédiat après paiement. Vous recevez vos identifiants par email avec toutes les instructions d'installation détaillées." } },
    { "@type": "Question", "name": "Est-ce compatible avec ma TV / smartphone ?", "acceptedAnswer": { "@type": "Answer", "text": "Oui, 100% compatible ! Fonctionne sur Smart TV (Samsung, LG, Sony), PC, Mac, smartphones Android et iOS, tablettes, box Android, Amazon Firestick, Chromecast, et bien plus encore." } },
    { "@type": "Question", "name": "Puis-je tester avant de payer ?", "acceptedAnswer": { "@type": "Answer", "text": "Absolument ! Nous offrons un test gratuit de 24h pour découvrir l'intégralité du service sans engagement." } },
    { "@type": "Question", "name": "La qualité est vraiment 4K ?", "acceptedAnswer": { "@type": "Answer", "text": "Oui ! Nos serveurs premium garantissent une diffusion en 4K HDR pour les films et grands événements sportifs." } },
    { "@type": "Question", "name": "Que se passe-t-il si j'ai un problème technique ?", "acceptedAnswer": { "@type": "Answer", "text": "Notre équipe support est disponible 24/7 via Telegram et email." } },
    { "@type": "Question", "name": "Y a-t-il une garantie sur les comptes ?", "acceptedAnswer": { "@type": "Answer", "text": "Oui, les comptes sont garantis. En cas de dysfonctionnement, nous remplaçons immédiatement votre compte sans frais supplémentaires." } }
  ]
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://roidustreaming.fr" }]
};

const Index = () => {
  useAffiliateTracking();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      
      <Header />
      <HeroSection />
      <PlatformsSection />
      
      <Suspense fallback={<SectionFallback />}>
        <PricingSection />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <PriceComparisonSection />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <TestimonialsSection />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <FaqSection />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <SeoContentSection />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Footer />
      </Suspense>
      
      <Suspense fallback={null}>
        <FloatingTestCTA />
      </Suspense>
    </div>
  );
};

export default Index;
